import React, { useState } from 'react';
import {
  Monitor,
  Tablet,
  Smartphone,
  Undo2,
  Redo2,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  Eye,
  Edit3,
  HelpCircle,
  X,
  Keyboard,
  Globe,
  FileText,
  Plus,
  Sparkles,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  History,
  Download,
  Layers,
  ChevronDown,
  Code,
  Paintbrush,
  Sliders,
  Grid,
  Trash2,
  Megaphone,
} from 'lucide-react';
import type { ViewportMode } from '../types';
import type { SyncStatus } from '../hooks/usePageDraftSync';

export interface StudioSiteItem {
  id: string;
  name: string;
  subdomain_slug: string;
}

export interface StudioPageItem {
  id: string;
  title: string;
  route_path: string;
}

export interface StudioToolbarProps {
  viewport: ViewportMode;
  onViewportChange: (vp: ViewportMode) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  syncStatus: SyncStatus;
  onPublish: () => void;
  mode?: 'edit' | 'preview';
  onModeChange?: (mode: 'edit' | 'preview') => void;
  // Site & Page Context
  sites?: StudioSiteItem[];
  activeSiteId?: string;
  onSelectSite?: (siteId: string) => void;
  onCreateSite?: () => void;
  pages?: StudioPageItem[];
  activePageId?: string;
  onSelectPage?: (pageId: string) => void;
  onCreatePage?: () => void;
  onManagePages?: () => void;
  // Secondary Actions
  onOpenTemplatesBundles?: () => void;
  onOpenPromotions?: () => void;
  onOpenHistory?: () => void;
  onOpenExportImport?: () => void;
  // Zen Mode
  isZenMode?: boolean;
  onToggleZenMode?: () => void;
  // GrapesJS Canvas Tools & Views
  showOutlines?: boolean;
  onToggleOutlines?: () => void;
  showSpacingGuides?: boolean;
  onToggleSpacingGuides?: () => void;
  zoom?: number;
  onZoomChange?: (z: number) => void;
  onOpenCodeModal?: () => void;
  onClearCanvas?: () => void;
  activeRightDockTab?: 'style' | 'traits' | 'layers';
  onRightDockTabChange?: (tab: 'style' | 'traits' | 'layers') => void;
  // Governance
  publishApprovalStatus?: 'draft' | 'pending_approval' | 'published';
  onRequestApproval?: () => void;
}

// StudioToolbar is the unified 52px header bar for Wix/Framer-grade website building
export function StudioToolbar({
  viewport,
  onViewportChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  syncStatus,
  onPublish,
  mode = 'edit',
  onModeChange,
  sites = [],
  activeSiteId = '',
  onSelectSite,
  onCreateSite,
  pages = [],
  activePageId = '',
  onSelectPage,
  onCreatePage,
  onManagePages,
  onOpenTemplatesBundles,
  onOpenPromotions,
  onOpenHistory,
  onOpenExportImport,
  isZenMode = false,
  onToggleZenMode,
  publishApprovalStatus = 'draft',
  showOutlines = true,
  onToggleOutlines,
  showSpacingGuides = false,
  onToggleSpacingGuides,
  zoom = 1,
  onZoomChange,
  onOpenCodeModal,
  onClearCanvas,
  activeRightDockTab = 'style',
  onRightDockTabChange,
}: StudioToolbarProps): React.JSX.Element {
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  return (
    <header className="h-13 shrink-0 border-b border-border bg-card/95 backdrop-blur-md flex items-center justify-between px-3 md:px-4 z-40 select-none transition-colors">
      {/* LEFT ZONE: Site & Page Switcher + Undo/Redo + Edit/Preview */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
        {/* Site Switcher */}
        {sites.length > 0 && onSelectSite && (
          <div className="flex items-center gap-1 bg-muted/60 hover:bg-muted px-2 py-1 rounded-lg border border-border/70 text-xs font-semibold text-foreground transition-colors">
            <Globe className="h-3.5 w-3.5 text-primary shrink-0" />
            <select
              value={activeSiteId}
              onChange={(e) => {
                if (e.target.value === '__NEW__') {
                  onCreateSite?.();
                } else {
                  onSelectSite(e.target.value);
                }
              }}
              className="bg-transparent border-none text-foreground focus:outline-none cursor-pointer max-w-[120px] md:max-w-[160px] truncate text-xs font-semibold"
              title="Switch Active Website"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id} className="bg-card text-foreground">
                  {s.name}
                </option>
              ))}
              {onCreateSite && (
                <option value="__NEW__" className="bg-card text-primary font-bold">
                  + Create New Site...
                </option>
              )}
            </select>
          </div>
        )}

        {/* Slash divider */}
        <span className="text-muted-foreground/40 font-mono text-sm hidden sm:inline">/</span>

        {/* Page Switcher */}
        {pages.length > 0 && onSelectPage && (
          <div className="flex items-center gap-1 bg-muted/60 hover:bg-muted px-2 py-1 rounded-lg border border-border/70 text-xs font-semibold text-foreground transition-colors">
            <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <select
              value={activePageId}
              onChange={(e) => {
                if (e.target.value === '__NEW__') {
                  onCreatePage?.();
                } else if (e.target.value === '__MANAGE__') {
                  onManagePages?.();
                } else {
                  onSelectPage(e.target.value);
                }
              }}
              className="bg-transparent border-none text-foreground focus:outline-none cursor-pointer max-w-[110px] md:max-w-[150px] truncate text-xs font-semibold"
              title="Switch Page"
            >
              {pages.map((p) => (
                <option key={p.id} value={p.id} className="bg-card text-foreground">
                  {p.title} ({p.route_path})
                </option>
              ))}
              {onCreatePage && (
                <option value="__NEW__" className="bg-card text-primary font-bold">
                  + Add New Page...
                </option>
              )}
              {onManagePages && (
                <option value="__MANAGE__" className="bg-card text-muted-foreground">
                  ⚙ Manage Pages &amp; SEO...
                </option>
              )}
            </select>
          </div>
        )}

        <div className="h-4 w-[1px] bg-border mx-1 hidden lg:block" />

        {/* Undo / Redo */}
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={onUndo}
            disabled={!canUndo}
            aria-label="Undo (Ctrl+Z)"
            title="Undo (Ctrl+Z)"
            className="p-1.5 rounded-md hover:bg-muted text-foreground disabled:opacity-30 disabled:pointer-events-none transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onRedo}
            disabled={!canRedo}
            aria-label="Redo (Ctrl+Y)"
            title="Redo (Ctrl+Y)"
            className="p-1.5 rounded-md hover:bg-muted text-foreground disabled:opacity-30 disabled:pointer-events-none transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
          >
            <Redo2 className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="h-4 w-[1px] bg-border mx-1 hidden md:block" />

        {/* Templates & Bundles Selector */}
        {onOpenTemplatesBundles && (
          <button
            type="button"
            onClick={onOpenTemplatesBundles}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-all cursor-pointer shadow-xs min-h-[36px]"
            title="Choose from 10 full website bundles or 50+ page templates"
          >
            <Sparkles className="h-3 w-3 text-amber-500" />
            <span className="hidden sm:inline">Templates &amp; Bundles</span>
          </button>
        )}

        {/* Promotional Tools Drawer Trigger */}
        {onOpenPromotions && (
          <button
            type="button"
            onClick={onOpenPromotions}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/30 transition-all cursor-pointer shadow-xs min-h-[36px]"
            title="Configure Notice Banners and Promotional Popup Modals"
            data-testid="studio-promotions-btn"
          >
            <Megaphone className="h-3 w-3 text-amber-500" />
            <span className="hidden sm:inline">Promotions</span>
          </button>
        )}

        <div className="h-4 w-[1px] bg-border mx-1 hidden md:block" />

        {/* Edit vs Preview Mode Toggle */}
        {onModeChange && (
          <div className="flex items-center gap-0.5 bg-muted/60 p-0.5 rounded-lg border border-border">
            <button
              type="button"
              onClick={() => onModeChange('edit')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                mode === 'edit'
                  ? 'bg-background text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Edit3 className="h-3 w-3" />
              <span className="hidden sm:inline">Edit</span>
            </button>
            <button
              type="button"
              onClick={() => onModeChange('preview')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                mode === 'preview'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Eye className="h-3 w-3" />
              <span className="hidden sm:inline">Preview</span>
            </button>
          </div>
        )}
      </div>

      {/* CENTER ZONE: Device Viewport Switcher + Zoom + GrapesJS Canvas Tools */}
      <div className="flex items-center gap-1.5 shrink-0">
        <div className="flex items-center gap-0.5 bg-muted/50 p-1 rounded-lg border border-border">
          <button
            type="button"
            onClick={() => onViewportChange('desktop')}
            className={`p-1.5 rounded-md transition-colors cursor-pointer ${
              viewport === 'desktop' ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Desktop View (1200px)"
          >
            <Monitor className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onViewportChange('tablet')}
            className={`p-1.5 rounded-md transition-colors cursor-pointer ${
              viewport === 'tablet' ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Tablet View (768px)"
          >
            <Tablet className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onViewportChange('mobile')}
            className={`p-1.5 rounded-md transition-colors cursor-pointer ${
              viewport === 'mobile' ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Mobile View (375px)"
          >
            <Smartphone className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Zoom Scale Selector */}
        {onZoomChange && (
          <select
            value={zoom}
            onChange={(e) => onZoomChange(parseFloat(e.target.value))}
            className="hidden lg:block text-[11px] font-mono bg-muted/50 border border-border rounded-lg px-2 py-1 text-muted-foreground cursor-pointer focus:outline-none"
            title="Canvas Zoom Scale"
          >
            <option value="0.5">50%</option>
            <option value="0.75">75%</option>
            <option value="1">100%</option>
            <option value="1.25">125%</option>
          </select>
        )}

        {/* GrapesJS Canvas Tools (Outlines sw-visibility, Spacing Guides, View Code) */}
        {mode !== 'preview' && (
          <div className="hidden md:flex items-center gap-0.5 bg-muted/50 p-1 rounded-lg border border-border">
            {onToggleOutlines && (
              <button
                type="button"
                onClick={onToggleOutlines}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  showOutlines ? 'bg-[#3b97e3]/15 text-[#3b97e3] font-bold shadow-xs' : 'text-muted-foreground hover:text-foreground'
                }`}
                title="View Component Outlines (sw-visibility)"
              >
                <Grid className="h-3.5 w-3.5" />
              </button>
            )}

            {onToggleSpacingGuides && (
              <button
                type="button"
                onClick={onToggleSpacingGuides}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  showSpacingGuides ? 'bg-amber-500/20 text-amber-600 font-bold shadow-xs' : 'text-muted-foreground hover:text-foreground'
                }`}
                title="Toggle Spacing Guides (Margin & Padding Overlays)"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            )}

            {onOpenCodeModal && (
              <button
                type="button"
                onClick={onOpenCodeModal}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-background transition-colors cursor-pointer"
                title="View Code & Export (HTML, CSS, AST)"
              >
                <Code className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* RIGHT ZONE: Sync Status + Right Dock Switcher + More Menu + Zen Mode + Publish */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {/* Right Dock Switcher Icons (Style, Traits, Layers) */}
        {mode !== 'preview' && onRightDockTabChange && (
          <div className="hidden lg:flex items-center gap-0.5 bg-muted/50 p-1 rounded-lg border border-border">
            <button
              type="button"
              onClick={() => onRightDockTabChange('style')}
              className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                activeRightDockTab === 'style' ? 'bg-background text-[#3b97e3] shadow-xs' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Style Manager"
            >
              <Paintbrush className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onRightDockTabChange('traits')}
              className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                activeRightDockTab === 'traits' ? 'bg-background text-[#3b97e3] shadow-xs' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Component Traits"
            >
              <Sliders className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onRightDockTabChange('layers')}
              className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                activeRightDockTab === 'layers' ? 'bg-background text-[#3b97e3] shadow-xs' : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Layer Navigator"
            >
              <Layers className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Auto-save status dot */}
        <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground px-1.5">
          {syncStatus === 'saving' && (
            <span className="flex items-center gap-1 text-amber-500">
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[11px] font-medium hidden md:inline">Saving...</span>
            </span>
          )}
          {syncStatus === 'saved' && (
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="text-[11px] font-medium hidden md:inline">Saved</span>
            </span>
          )}
          {syncStatus === 'error' && (
            <span className="flex items-center gap-1 text-rose-500">
              <AlertCircle className="h-3 w-3" />
              <span className="text-[11px] font-medium hidden md:inline">Save Error</span>
            </span>
          )}
        </div>

        {/* More Actions Popover */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMoreMenu((v) => !v)}
            className="p-1.5 rounded-lg border border-border/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors min-h-[34px] min-w-[34px] flex items-center justify-center cursor-pointer"
            title="More Options (History, Export/Import, Shortcuts)"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>

          {showMoreMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMoreMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1.5 w-52 bg-card border border-border rounded-xl shadow-xl p-1.5 z-50 text-xs flex flex-col gap-0.5 animate-in fade-in slide-in-from-top-1 duration-100">
                {onOpenHistory && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenHistory();
                    }}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md hover:bg-muted text-foreground text-left transition-colors"
                  >
                    <History className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Revision History &amp; Diff</span>
                  </button>
                )}

                {onOpenPromotions && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenPromotions();
                    }}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md hover:bg-muted text-foreground text-left transition-colors"
                  >
                    <Megaphone className="h-3.5 w-3.5 text-amber-500" />
                    <span>Promotions &amp; Announcements</span>
                  </button>
                )}

                {onOpenExportImport && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenExportImport();
                    }}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md hover:bg-muted text-foreground text-left transition-colors"
                  >
                    <Download className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Export / Import Bundle</span>
                  </button>
                )}

                {onClearCanvas && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onClearCanvas();
                    }}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md hover:bg-rose-500/10 text-rose-500 text-left transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Clear Canvas</span>
                  </button>
                )}

                <div className="h-[1px] bg-border my-1" />

                <button
                  type="button"
                  onClick={() => {
                    setShowMoreMenu(false);
                    setShowShortcutsModal(true);
                  }}
                  className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md hover:bg-muted text-foreground text-left transition-colors"
                >
                  <Keyboard className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Keyboard Shortcuts</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Zen Mode / Fullscreen Toggle */}
        {onToggleZenMode && (
          <button
            type="button"
            onClick={onToggleZenMode}
            className={`p-1.5 rounded-lg border transition-colors min-h-[34px] min-w-[34px] flex items-center justify-center cursor-pointer ${
              isZenMode
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border/80 hover:bg-muted text-muted-foreground hover:text-foreground'
            }`}
            title={isZenMode ? 'Exit Zen Mode (Full View)' : 'Zen Focus Studio (Hide outer navigation)'}
          >
            {isZenMode ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        )}

        {/* Primary Publish CTA */}
        <button
          type="button"
          onClick={onPublish}
          disabled={syncStatus === 'publishing'}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-bold text-xs hover:opacity-90 transition-opacity disabled:opacity-50 min-h-[34px] cursor-pointer shadow-sm"
        >
          <UploadCloud className="h-3.5 w-3.5" />
          <span>Publish</span>
        </button>
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showShortcutsModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-sm w-full p-5 shadow-2xl flex flex-col gap-4 text-left animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <div className="flex items-center gap-2">
                <Keyboard className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-bold text-foreground">Keyboard Shortcuts</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowShortcutsModal(false)}
                className="p-1 rounded hover:bg-muted text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Undo action</span>
                <kbd className="px-2 py-0.5 bg-muted rounded border border-border text-[11px] font-mono">Ctrl + Z</kbd>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Redo action</span>
                <kbd className="px-2 py-0.5 bg-muted rounded border border-border text-[11px] font-mono">Ctrl + Y</kbd>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Duplicate selected block</span>
                <kbd className="px-2 py-0.5 bg-muted rounded border border-border text-[11px] font-mono">Ctrl + D</kbd>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Delete selected block</span>
                <kbd className="px-2 py-0.5 bg-muted rounded border border-border text-[11px] font-mono">Delete / Backspace</kbd>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Save draft</span>
                <kbd className="px-2 py-0.5 bg-muted rounded border border-border text-[11px] font-mono">Ctrl + S</kbd>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-muted-foreground">Deselect block</span>
                <kbd className="px-2 py-0.5 bg-muted rounded border border-border text-[11px] font-mono">Esc</kbd>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowShortcutsModal(false)}
              className="w-full mt-2 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 transition-opacity"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
